import {
  CADENCE_COLORS,
  CLIMBING_SPEED_COLORS,
  DESCENDING_SPEED_COLORS,
  ELEVATION_COLOR,
  HEART_RATE_COLORS,
  PACE_COLORS,
} from './colorScale';
import type { ActivityTab } from './recordingActivity';

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** The small picture beside a row: the graph's line in its colours, or the grey elevation behind it. */
export type ChartInfoArt = 'line' | 'shape';

/** [lead] is drawn bold, with [body] after it. */
export interface ChartInfoRow {
  art: ChartInfoArt;
  lead: string;
  body: string;
}

export interface ChartInfo {
  title: string;
  summary: string;
  /** The line picture's colours, low to high as the graph draws them. */
  lineColors: string[];
  rows: ChartInfoRow[];
}

/** What the info button explains about [tab]'s graph; the same copy the apps show. */
export function chartInfo(tab: ActivityTab, t: Translate): ChartInfo {
  const line = (lead: string, body: string): ChartInfoRow => ({ art: 'line', lead, body });
  const shape = (body = t('recordings.activity.chart_info.shape_body')): ChartInfoRow => ({
    art: 'shape',
    lead: t('recordings.activity.chart_info.shape_lead'),
    body,
  });
  const colour = t('recordings.activity.chart_info.colour_lead');

  switch (tab) {
    case 'elevation':
      return {
        title: t('recordings.activity.chart_info.elevation_title'),
        summary: t('recordings.activity.chart_info.elevation_summary'),
        lineColors: [ELEVATION_COLOR],
        rows: [line(t('recordings.activity.chart_info.elevation_line_lead'), t('recordings.activity.chart_info.elevation_line_body'))],
      };
    case 'ascent':
      return {
        title: t('recordings.activity.chart_info.ascent_title'),
        summary: t('recordings.activity.chart_info.ascent_summary'),
        lineColors: [CLIMBING_SPEED_COLORS[2], DESCENDING_SPEED_COLORS[0], DESCENDING_SPEED_COLORS[1]],
        rows: [
          line(t('recordings.activity.chart_info.ascent_line_lead'), t('recordings.activity.chart_info.ascent_line_body')),
          shape(t('recordings.activity.chart_info.ascent_shape_body')),
        ],
      };
    case 'heartRate':
      return {
        title: t('recordings.activity.chart_info.heart_rate_title'),
        summary: t('recordings.activity.chart_info.heart_rate_summary'),
        lineColors: [HEART_RATE_COLORS.low, HEART_RATE_COLORS.high],
        rows: [line(colour, t('recordings.activity.chart_info.heart_rate_colour_body')), shape()],
      };
    case 'steps':
      return {
        title: t('recordings.activity.chart_info.steps_title'),
        summary: t('recordings.activity.chart_info.steps_summary'),
        lineColors: [CADENCE_COLORS.low, CADENCE_COLORS.high],
        rows: [line(colour, t('recordings.activity.chart_info.steps_colour_body')), shape()],
      };
    case 'pace':
      return {
        title: t('recordings.activity.chart_info.pace_title'),
        summary: t('recordings.activity.chart_info.pace_summary'),
        lineColors: [PACE_COLORS.low, PACE_COLORS.high],
        rows: [line(t('recordings.activity.chart_info.pace_line_lead'), t('recordings.activity.chart_info.pace_line_body')), shape()],
      };
  }
}

/** A CSS gradient through [colors]; one colour draws flat. */
export function lineGradient(colors: string[]): string {
  const stops = colors.length === 1 ? [colors[0], colors[0]] : colors;
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}
